import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Select, Text, Skeleton } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { useGoogleAdsCampaigns, useGoogleAdsAccount } from '@/hooks/useGoogleAds';
import { microsToAmount, formatPercentage } from '@/types/googleAds';
import type { DateRangeOption, Campaign } from '@/types/googleAds';
import { StatusBadge } from '../components/StatusBadge';

interface CampaignsTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

const CAMPAIGN_TYPES = [
  { value: '', label: 'Alle Typen' },
  { value: 'SEARCH', label: 'Search' },
  { value: 'DISPLAY', label: 'Display' },
  { value: 'SHOPPING', label: 'Shopping' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'PERFORMANCE_MAX', label: 'Performance Max' },
  { value: 'DEMAND_GEN', label: 'Demand Gen' },
];

export function CampaignsTab({ customerId, dateRange }: CampaignsTabProps) {
  const { data: campaigns, isLoading } = useGoogleAdsCampaigns(customerId, dateRange);
  const { data: account } = useGoogleAdsAccount(customerId);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Campaign>>({
    columnAccessor: 'metrics.costMicros',
    direction: 'desc',
  });

  const formatCurrency = (micros: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(
      microsToAmount(micros),
    );

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num);

  const filteredCampaigns = campaigns
    ?.filter((c) => {
      if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter && c.type !== typeFilter) return false;
      if (statusFilter && c.status !== statusFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = sortStatus.columnAccessor;
      let aVal: number | string;
      let bVal: number | string;

      if (accessor.startsWith('metrics.')) {
        const key = accessor.replace('metrics.', '') as keyof Campaign['metrics'];
        aVal = a.metrics[key] ?? 0;
        bVal = b.metrics[key] ?? 0;
      } else if (accessor === 'budget.amountMicros') {
        aVal = a.budget.amountMicros;
        bVal = b.budget.amountMicros;
      } else {
        aVal = a[accessor as keyof Campaign] as string;
        bVal = b[accessor as keyof Campaign] as string;
      }

      if (typeof aVal === 'string') {
        return sortStatus.direction === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal);
      }
      return sortStatus.direction === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Kampagne suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={250}
          />
          <Select
            placeholder="Kampagnentyp"
            data={CAMPAIGN_TYPES}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v ?? '')}
            clearable
            w={180}
          />
          <Select
            placeholder="Status"
            data={[
              { value: '', label: 'Alle Status' },
              { value: 'ENABLED', label: 'Aktiv' },
              { value: 'PAUSED', label: 'Pausiert' },
              { value: 'REMOVED', label: 'Entfernt' },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v ?? '')}
            clearable
            w={150}
          />
          <Text size="sm" c="dimmed" ml="auto">
            {filteredCampaigns?.length ?? 0} Kampagnen
          </Text>
        </Group>
      </Paper>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        records={filteredCampaigns ?? []}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          {
            accessor: 'name',
            title: 'Kampagne',
            sortable: true,
            width: 250,
            render: (campaign) => (
              <div>
                <Text size="sm" fw={500} lineClamp={1}>
                  {campaign.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {campaign.type}
                </Text>
              </div>
            ),
          },
          {
            accessor: 'status',
            title: 'Status',
            sortable: true,
            width: 100,
            render: (campaign) => <StatusBadge status={campaign.status} />,
          },
          {
            accessor: 'budget.amountMicros',
            title: 'Budget/Tag',
            sortable: true,
            width: 120,
            render: (campaign) => formatCurrency(campaign.budget.amountMicros),
          },
          {
            accessor: 'biddingStrategy.type',
            title: 'Gebotsstrategie',
            width: 150,
            render: (campaign) => (
              <Text size="sm" lineClamp={1}>
                {campaign.biddingStrategy.type.replace(/_/g, ' ')}
              </Text>
            ),
          },
          {
            accessor: 'metrics.impressions',
            title: 'Impressionen',
            sortable: true,
            textAlign: 'right',
            width: 110,
            render: (campaign) => formatNumber(campaign.metrics.impressions),
          },
          {
            accessor: 'metrics.clicks',
            title: 'Klicks',
            sortable: true,
            textAlign: 'right',
            width: 80,
            render: (campaign) => formatNumber(campaign.metrics.clicks),
          },
          {
            accessor: 'metrics.ctr',
            title: 'CTR',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (campaign) => formatPercentage(campaign.metrics.ctr),
          },
          {
            accessor: 'metrics.costMicros',
            title: 'Kosten',
            sortable: true,
            textAlign: 'right',
            width: 100,
            render: (campaign) => formatCurrency(campaign.metrics.costMicros),
          },
          {
            accessor: 'metrics.conversions',
            title: 'Conv.',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (campaign) => campaign.metrics.conversions.toFixed(1),
          },
          {
            accessor: 'metrics.costPerConversion',
            title: 'CPA',
            sortable: true,
            textAlign: 'right',
            width: 90,
            render: (campaign) =>
              campaign.metrics.costPerConversion
                ? formatCurrency(campaign.metrics.costPerConversion * 1_000_000)
                : '-',
          },
          {
            accessor: 'metrics.roas',
            title: 'ROAS',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (campaign) => (campaign.metrics.roas ? `${campaign.metrics.roas.toFixed(2)}x` : '-'),
          },
          {
            accessor: 'metrics.impressionShare',
            title: 'IS',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (campaign) => formatPercentage(campaign.metrics.impressionShare),
          },
        ]}
        noRecordsText="Keine Kampagnen gefunden"
        minHeight={400}
      />
    </Stack>
  );
}
